/*
 * Phone wizard input component
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';

import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';

@Component ( {
    selector: 'reporter-phone',
    templateUrl: './phone.component.html',
    styleUrls: ['./phone.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PhoneComponent),
            multi: true
        },
    ],
    standalone: false
} )

export class PhoneComponent implements ControlValueAccessor, Validator, AfterViewInit {

  @Input () placeholder: string;

  constructor( private renderer: Renderer2, private elementRef: ElementRef ) {
  }

  set value( v: any ) {
    if ( v !== this.innerValue ) {
      this.innerValue = v;
      this.onChange ( v );
    }
  }

  get value() {
    return `${ this.innerValue }`.replace ( /\D/g, '' );
  }

  get required(): boolean {
    return this.elementRef.nativeElement.attributes.hasOwnProperty ( 'required' );
  }

  innerValue: any;
  initialized = false;
  initialValue: any;

  @ViewChild ( 'field', { static: true } ) field: ElementRef;
  onChange = ( _: any ) => {
  }
  onTouched = () => {
  }

  ngAfterViewInit() {
    this.initialValue = this.value;
    if ( this.required ) {
      this.renderer.setAttribute ( this.field.nativeElement, 'required', '' );
    }
    if ( this.elementRef.nativeElement.classList.contains ( 'required' ) ) {
      this.renderer.addClass ( this.field.nativeElement, 'required' );
    }
    this.initialized = true;
  }

  writeValue( value: any ): void {
    let newVal = `${ value }`.replace ( /\D/g, '' );
    if ( newVal.length === 0 ) {
      newVal = '';
    } else if ( newVal.length <= 3 ) {
      newVal = newVal.replace ( /^(\d{0,3})/, '($1' );
    } else if ( newVal.length <= 6 ) {
      newVal = newVal.replace ( /^(\d{0,3})(\d{0,3})/, '($1) $2' );
    } else if ( newVal.length <= 10 ) {
      newVal = newVal.replace ( /^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3' );
    } else {
      newVal = newVal.substring ( 0, 10 );
      newVal = newVal.replace ( /^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3' );
    }

    const numeric = parseInt ( newVal.replace ( /\D/g, '' ), 10 );

    this.innerValue = newVal;
    this.renderer.setProperty ( this.elementRef.nativeElement, 'value', numeric || undefined );
    this.renderer.setProperty ( this.field.nativeElement, 'value', newVal );
    this.onChange ( numeric || undefined );
  }

  registerOnChange( fn: ( value: any ) => any ): void {
    this.onChange = fn;
  }

  registerOnTouched( fn: () => any ): void {
    this.onTouched = fn;
  }

  setDisabledState( isDisabled: boolean ): void {
    this.renderer.setProperty ( this.elementRef.nativeElement, 'disabled', isDisabled );
  }

  validate( control: FormControl ): ValidationErrors {
    if ( !this.initialized ) {
      return null;
    }

    if ( !control.value && this.required ) {
      return {
        required: true,
        message: ' is required.'
      };
    }
    if ( control.value !== null && control.value !== undefined && control.value < 1000000000 && this.initialValue !== control.value ) {
      return {
        badInput: true,
        message: ' must be a valid phone number.',
      };
    }
    return null;
  }
}
